import {
  GET_ASC_CONTRIBUTOR_PROJECT_ALLOCATIONS,
  AscContributorProjectAllocationFilter,
  AscContributorProjectAllocationSort,
  GetAscContributorProjectAllocationsQuery,
  GetAscContributorProjectAllocationsQueryVariables,
  PeriodName,
  useCursorPaginatedQuery,
  Conditions,
  advancedFilter,
  FilterField,
  keywordFilter,
  useSticky,
} from '@neo.tax/shared';

export type AscContributorProjectAllocation =
  GetAscContributorProjectAllocationsQuery['getAscContributorProjectAllocations']['edges'][number]['node'];

interface UseAscContributorProjectAllocationsProps {
  filterConditions?: Conditions<FilterField<AscContributorProjectAllocationFilter>>;
  numElementsInitialFetch?: number;
  contributorId: string;
  fiscalYearId: string;
  periodName: PeriodName;
  skip?: boolean;
  sorting?: Array<AscContributorProjectAllocationSort> | AscContributorProjectAllocationSort;
  searchKeyword?: string | null;
}

export const useAscContributorProjectAllocations = ({
  contributorId,
  filterConditions,
  fiscalYearId,
  numElementsInitialFetch,
  periodName,
  searchKeyword,
  skip = false,
  sorting,
}: UseAscContributorProjectAllocationsProps): {
  loading: boolean;
  ascContributorProjectAllocations: AscContributorProjectAllocation[] | null;
  hasNextPage: boolean;
  onLoadMoreItems: () => Promise<void>;
} => {
  const {
    data: requestData,
    error,
    hasNextPage,
    loading,
    onLoadMoreItems,
  } = useCursorPaginatedQuery<
    GetAscContributorProjectAllocationsQuery,
    GetAscContributorProjectAllocationsQueryVariables
  >(
    GET_ASC_CONTRIBUTOR_PROJECT_ALLOCATIONS,
    {
      variables: {
        contributorId,
        fiscalYearId,
        periodName,
        filter: {
          and: [
            advancedFilter<AscContributorProjectAllocationFilter>(filterConditions),
            keywordFilter(['ascProjectName'], searchKeyword),
          ],
        },
        sorting: sorting ?? [],
      },
      skip,
    },
    {
      getConnection: (data) => data.getAscContributorProjectAllocations,
      numElementsPerFetch: 10,
      numElementsInitialFetch,
    },
  );

  // Keep existing data on the screen while a new query is running, so it
  // doesn't keep clearing the screen as you type into the filter.
  const stickyData = useSticky(requestData);
  const ascContributorProjectAllocations =
    stickyData?.getAscContributorProjectAllocations?.edges.map((edge) => edge.node) ?? null;

  if (loading && !ascContributorProjectAllocations) {
    return {
      loading: true,
      ascContributorProjectAllocations: null,
      hasNextPage: false,
      onLoadMoreItems: () => Promise.resolve(),
    };
  }
  if (error) {
    throw error;
  }

  if (!stickyData) {
    return {
      loading: true,
      ascContributorProjectAllocations: null,
      hasNextPage: false,
      onLoadMoreItems: () => Promise.resolve(),
    };
  }

  return {
    loading,
    ascContributorProjectAllocations,
    hasNextPage,
    onLoadMoreItems,
  };
};
